import { isClonedVoiceCode } from '@vbee-holding/aivoice-shared-lib';
import React, { memo, useEffect, useMemo, useRef, useState } from 'react';
import classNames from 'classnames';
import moment from 'moment';
import { Button, Checkbox, IconButton, Typography } from '@mui/material';
import {
  KeyboardArrowDown as ArrowDropDownIcon,
  KeyboardArrowUp as ArrowUpIcon,
  WarningRounded as WarningRoundedIcon,
  CallMerge as MergeIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import { OrderedSet } from 'immutable';
import { BREAK_LINE_DELIMITER } from '@src/constants';
import {
  CONVERTING_STATUS,
  DIRECTION,
  MAX_CHARACTER_PER_BLOCK,
  BLOCK_STATUS,
} from '@src/constants/project';
import {
  ContentState,
  Editor,
  EditorState,
  Modifier,
  getDefaultKeyBinding,
} from 'draft-js';
import sanitize from 'sanitize-html';
import Tooltip from '@src/components/Tooltip';
import BadgeAvatar from '@src/components/BadgeAvatar';
import { COLOR } from '@src/styles/color';
import addBlock from '@src/assets/icons/add-block.svg';
import {
  BREAK_TIME_REGEX,
  DEFAULT_BREAK_TIME,
  DEFAULT_SPEED,
  VOICE_OWNERSHIP,
} from '@src/constants/voice';
import { v4 as uuidv4 } from 'uuid';
import { countTextLength } from '@src/services/tts';
import BreakTimeHighlight from '@src/containers/TTS/BreakTimeHightLight';
import { generateDecorator } from '@src/services/editor';
import { MAX_CHARACTER_LENGTH } from '@src/constants/tts';
import actions from '@src/redux/actions';
import {
  downloadFileByUrl,
  // downloadFileUsingUrl,
  getFileName,
} from '@src/utils/file';
import { LANDING_PAGE_URL } from '@src/configs';
import { CATEGORY_TYPE, PLATFORM_TYPE } from '@src/constants/support';
import { getPresignedAudioUrl } from '@src/apis/requests';
import { trackBlockAudioDownloaded } from '@src/services/moengage';
import {
  getBlockStatus,
  isBlockChanged,
  formatInvalidTexts,
  checkBlockTextError,
} from '@src/services/project';
import VoiceSelectionDialog from '@src/containers/VoiceSelectionDialog';
import CommonButton from '@src/new-components/Button';
import NewTypography from '@src/new-components/Typography';
import {
  StyledAddNewBlockZone,
  StyledBlockEditor,
  StyledContentBlock,
  StyledContentBlockWrapper,
  StyledInfoBlock,
  StyledOrdinalNumberBlock,
} from './index.style';
import MoreOptionBlock from '../../component/MoreOptionBlock';
import SynthesisButton from '../../component/SynthesisButton';
import ConfigOptionBlock from '../../component/ConfigOptionBlock';
import CreditsCount from '../../component/CreditsCount';
import EditNameBlock from './EditNameBlock';

const ContentBlock = ({
  blockId,
  block,
  characterExceed,
  onAddNewParagraphs,
  isLastBlock,
  isFocusing,
  onFocusingBlock,
  onDragEnd,
  disabledMoveUp,
  onSaveProject,
  voiceDefault,
  isPlayingBlock,
  isDownloading,
  onOpenOutOfCreditsDialog,
  onOpenWrongSyntaxDialog,
  onOpenWrongSyntaxDialogV2,
  onOpenDiscardedVoiceCloningDialog,
  checkValidCreateSynthesis,
  isHighlight,
  handlePlayAudio,
  projectId,
  originalBlock,
  onConvertBlockToAudio,
  audioType,
  isPlaying,
  setDeleteDialogData,
  onMergeBlock,
  nextBlock,
  areBlocksConsecutive = true,
  enableCreditUsagePreview,
}) => {
  const { t, i18n } = useTranslation();

  const { language } = i18n;
  const inputRef = useRef(null);
  const userId = useSelector((state) => state.auth.user?.id);

  const currentFilteringStatus = useSelector(
    (state) => state.project.currentFilteringStatus,
  );
  const currentFilteringVoiceCode = useSelector(
    (state) => state.project.currentFilteringVoiceCode,
  );
  const blockIds = useSelector((state) => state.project.multiBlock?.blockIds);

  const indexBlock = useMemo(() => {
    const idx = blockIds.indexOf(blockId) + 1;
    return idx;
  }, [blockIds, blockId]);

  const { filterVoice } = useSelector((state) => state.voice);
  const dispatch = useDispatch();

  const {
    voiceCode = voiceDefault?.code,
    text = '',
    speed = DEFAULT_SPEED,
    breakTime = DEFAULT_BREAK_TIME,
    audioLink,
    status,
    requestId,
    showSuccessIcon,
    audioExpiredAt,
    voice = voiceDefault,
    isBlockEditting,
    title = '',
  } = block;

  // Seperately destructuring hasEmphasis and emphasis since hasEmphasis is dependent on voice?.hasEmphasis
  const { hasEmphasis = voice?.hasEmphasis, emphasis = 0 } = block;

  const maxCharLimit = MAX_CHARACTER_PER_BLOCK;

  const blockStatus = getBlockStatus({
    block,
    originalBlock,
    audioType,
    maxCharLimit,
    voice,
  });

  const {
    exceedMaxChar: exceedBlockMaxChar,
    invalidCharacterLengthTexts,
    hasInvalidCharacterLength,
    hasInvalidCharacters,
  } = checkBlockTextError(text, maxCharLimit, [], voice);

  const getBlockErrorConfig = () => {
    const sanitizeOptions = {
      allowedTags: ['br', 'p', 'strong'],
      allowedAttributes: {
        p: ['style'],
        br: ['style'],
      },
    };

    if (exceedBlockMaxChar) {
      return {
        message: t('exceedBlockMaxChar', {
          maxCredits: maxCharLimit.toLocaleString(language),
        }),
        sanitizeOptions,
      };
    }
    if (hasInvalidCharacterLength) {
      return {
        message: t('invalidCharLengthMessage', {
          invalidTexts: formatInvalidTexts(invalidCharacterLengthTexts),
          maxCharacterLength: MAX_CHARACTER_LENGTH - 1,
        }),
        sanitizeOptions,
      };
    }
    if (hasInvalidCharacters) {
      return {
        message: t('invalidCharacter', { invalidText: text.trim() }),
        sanitizeOptions,
      };
    }
    return { message: '', sanitizeOptions };
  };

  const isSynthesizing =
    isDownloading || status === CONVERTING_STATUS.IN_PROGRESS;
  const isNextBlockSynthesizing =
    nextBlock?.status === CONVERTING_STATUS.IN_PROGRESS;
  const hasSuccessSynthesized =
    audioLink && status === CONVERTING_STATUS.SUCCESS;
  const isAudioExpired = audioExpiredAt && moment().isAfter(audioExpiredAt);
  const cannotMerge =
    !nextBlock ||
    isNextBlockSynthesizing ||
    isSynthesizing ||
    !areBlocksConsecutive;

  const [content, setContent] = useState(
    EditorState.createEmpty(
      generateDecorator(BREAK_TIME_REGEX, BreakTimeHighlight),
    ),
  );
  const [openVoices, setOpenVoices] = useState(false);

  const [openPopperBlockOption, setOpenPopperBlockOption] = useState(false);

  const handleOpenVoices = () => {
    const voiceOwnershipFilter = filterVoice?.voiceOwnership;
    const isClonedVoice = isClonedVoiceCode(voice?.code);
    let voiceOwnership = isClonedVoice
      ? VOICE_OWNERSHIP.PERSONAL
      : VOICE_OWNERSHIP.VBEE;

    if (
      isClonedVoice &&
      (voiceOwnershipFilter === VOICE_OWNERSHIP.COMMUNITY ||
        voice?.userId !== userId)
    )
      voiceOwnership = VOICE_OWNERSHIP.COMMUNITY;

    if (!voiceOwnershipFilter || voiceOwnershipFilter !== voiceOwnership)
      dispatch(
        actions.voice.changeFilterVoice({
          ...filterVoice,
          voiceOwnership,
        }),
      );

    setOpenVoices(true);
  };
  const handleCloseVoices = () => setOpenVoices(false);

  const handleOpenDeleteDialog = () => {
    setDeleteDialogData(blockId);
  };

  const getSelectionOffset = (editorState) => {
    // Get the current content and selection states
    const contentState = editorState.getCurrentContent();
    const selectionState = editorState.getSelection();

    // Get the key of the block where the selection starts
    const startKey = selectionState.getStartKey();

    // Get the offset within the starting block
    const startOffset = selectionState.getStartOffset();

    // Get a list of all content blocks
    const blockMap = contentState.getBlockMap();
    const blocks = blockMap.toList();

    let characterOffset = 0;

    // Find the index of the start block in the list
    const startBlockIndex = blocks.findIndex(
      (blockItem) => blockItem.getKey() === startKey,
    );

    // Iterate through all blocks before the start block and add their length + 1 (for the newline)
    for (let i = 0; i < startBlockIndex; i += 1) {
      characterOffset += blocks.get(i).getLength() + 1;
    }

    // Add the offset within the start block
    characterOffset += startOffset;

    return characterOffset;
  };

  // Check if cursor is in valid position for splitting
  const canSplitBlock = useMemo(() => {
    if (!text || isSynthesizing) return false;

    const offset = getSelectionOffset(content);

    // Must have text before and after cursor
    return offset > 0 && offset < text.length;
  }, [content, text, isSynthesizing, isLastBlock]);

  const isCheckBlockChanged = useMemo(() => {
    const withoutExtension = block?.audioLink?.split('.')?.pop();
    const currentBlock = {
      text: block?.text,
      voiceCode: block?.voiceCode,
      speed: block?.speed,
      audioType: withoutExtension,
      emphasis: block?.emphasis,
    };
    const blockOriginal = {
      text: originalBlock?.text,
      voiceCode: originalBlock?.voiceCode,
      speed: originalBlock?.speed,
      audioType,
      emphasis: originalBlock?.emphasis,
    };

    return isBlockChanged(currentBlock, blockOriginal);
  }, [block, originalBlock, audioType]);

  const isDisableConvertBtn = useMemo(
    () =>
      !text ||
      countTextLength(text) > maxCharLimit ||
      hasInvalidCharacterLength ||
      hasInvalidCharacters,
    [text, maxCharLimit, hasInvalidCharacterLength, hasInvalidCharacters],
  );

  const isDisablePlayAudio = useMemo(
    () => isCheckBlockChanged || !hasSuccessSynthesized || isAudioExpired,
    [isCheckBlockChanged, hasSuccessSynthesized, isAudioExpired],
  );

  const isFilteringBlockStatus = useMemo(
    () => currentFilteringStatus !== BLOCK_STATUS.ALL,
    [currentFilteringStatus],
  );

  const isFilteringBlockVoice = useMemo(
    () => currentFilteringVoiceCode !== '',
    [currentFilteringVoiceCode],
  );

  const handleChangeContent = (newContent) => setContent(newContent);

  // Handle split block action
  const handleSplitBlock = () => {
    if (!canSplitBlock) return;

    const contentState = content.getCurrentContent();
    const plainText = contentState.getPlainText();
    const offset = getSelectionOffset(content);
    // Split text at cursor position
    const textBefore = plainText.substring(0, offset);
    const textAfter = plainText.substring(offset);
    // Check if cursor is at the start of a block

    // Update current block with text before cursor
    dispatch(
      actions.project.updateBlock(blockId, { text: textBefore?.trim() }),
    );

    // Update editor state for current block
    const newContentState = ContentState.createFromText(textBefore?.trim());
    const newEditorState = EditorState.createWithContent(
      newContentState,
      generateDecorator(BREAK_TIME_REGEX, BreakTimeHighlight),
    );
    handleChangeContent(newEditorState);

    // Create new block with text after cursor
    const newBlockId = uuidv4();
    const newBlock = {
      voice,
      voiceCode,
      speed,
      breakTime,
      id: newBlockId,
      text: textAfter?.trim(),
      blockStatus: BLOCK_STATUS.DRAFT,
      emphasis,
      hasEmphasis,
    };

    // Add new block after current block
    onAddNewParagraphs(blockId, [newBlock]);

    // Focus on new block after it's added
    setTimeout(() => {
      onFocusingBlock(newBlockId);
    }, 100);
  };

  // Handle adding new block via Ctrl+Enter (separate from split block feature)
  const handleAddNewBlockOnShortcut = () => {
    if (isSynthesizing) return;

    // Calculate timing for dubbing projects

    // Create new empty block with same config as current block
    const newBlockId = uuidv4();
    const newBlock = {
      voice,
      voiceCode,
      speed,
      breakTime,
      id: newBlockId,
      text: '',
      blockStatus: BLOCK_STATUS.DRAFT,
      emphasis,
      hasEmphasis,
    };

    // Add new block after current block
    onAddNewParagraphs(blockId, [newBlock]);

    // Focus on new block after it's added
    onFocusingBlock(newBlockId);
  };

  // Handle keyboard shortcuts for adding new block
  const keyBindingFn = (e) => {
    // Detect Ctrl+Enter (Windows/Linux) or Cmd+Enter (Mac)
    if (
      e.keyCode === 13 && // Enter key
      (e.ctrlKey || e.metaKey) // Ctrl or Cmd key
    ) {
      return 'add-new-block-command';
    }
    return getDefaultKeyBinding(e);
  };

  const handleKeyCommand = (command) => {
    if (command === 'add-new-block-command') {
      handleAddNewBlockOnShortcut();
      return 'handled';
    }
    return 'not-handled';
  };

  const handleChangeVoice = (newVoice) => {
    dispatch(
      actions.project.updateBlock(blockId, {
        hasEmphasis: newVoice?.hasEmphasis,
        emphasis: 0,
        voiceCode: newVoice?.code,
        voice: {
          code: newVoice?.code,
          name: newVoice?.name,
          roundImage: newVoice?.roundImage,
          language: {
            roundImage: newVoice?.language?.roundImage,
          },
          level: newVoice?.level,
          provider: newVoice?.provider,
          eolDate: newVoice?.eolDate,
          creditFactor: newVoice?.creditFactor,
          canonicalVoice: newVoice?.canonicalVoice,
          discardAt: newVoice?.discardAt,
          retentionDays: newVoice?.retentionDays,
          status: newVoice?.status,
          userId: newVoice?.userId,
          features: Array.isArray(newVoice?.features) ? newVoice.features : [],
          labels: newVoice?.labels,
        },
      }),
    );
  };

  const handleChangeSpeed = (field, value) => {
    dispatch(actions.project.updateBlock(blockId, { speed: value }));
  };

  const handleChangeEmphasis = (field, value) => {
    dispatch(actions.project.updateBlock(blockId, { emphasis: value }));
  };

  const handleChangeBreakTime = (value) => {
    const currentContent = content.getCurrentContent();
    const selection = content.getSelection();
    const label = `<break time=${value}s/>`;
    const textWithEntity = Modifier.insertText(
      currentContent,
      selection,
      label,
      OrderedSet.of('BOLD'),
    );

    dispatch(
      actions.project.updateBlock(blockId, {
        breakTime: value,
        text: textWithEntity.getPlainText(),
      }),
    );
    handleChangeContent(
      EditorState.push(content, textWithEntity, 'insert-characters'),
    );
  };

  const handleChangeText = (value) => {
    dispatch(actions.project.updateBlock(blockId, { text: value }));
  };

  const handleEditorChange = (editorState) => {
    const selectionState = editorState.getSelection();
    if (!selectionState.toJS().hasFocus) return;
    const contentState = editorState.getCurrentContent();
    const plainText = contentState.getPlainText('\n');
    let newContent = editorState;

    const isContentChanged = !content
      .getCurrentContent()
      .equals(editorState.getCurrentContent());
    if (isContentChanged) {
      const currentStyle = editorState.getCurrentInlineStyle();
      if (currentStyle && currentStyle.size) {
        newContent = EditorState.setInlineStyleOverride(
          editorState,
          OrderedSet.of(),
        );
      }
      handleChangeContent(newContent);
      handleChangeText(plainText);
    } else {
      handleChangeContent(editorState);
    }
  };

  const handleAddNewParagraphs = () => {
    onAddNewParagraphs(blockId);
  };

  const handlePastedText = (txt) => {
    const splitParagraphs = txt.split(BREAK_LINE_DELIMITER);
    if (splitParagraphs.length > 1) {
      const selectionState = content.getSelection();
      const currentContent = content.getCurrentContent();

      // Insert pasted text at cursor position (or replace selected text)
      const textWithEntity = Modifier.replaceText(
        currentContent,
        selectionState,
        txt,
      );

      // Get the updated text with the pasted content
      const updatedText = textWithEntity.getPlainText('\n');
      const paragraphs = updatedText
        .split(BREAK_LINE_DELIMITER)
        .filter((p) => p.trim());

      // First paragraph is a combination of text before cursor + first part of pasted text
      const firstParagraphText = paragraphs.shift();

      // Create new blocks for the remaining paragraphs plus the text that was after the cursor
      const newBlocks = [];

      // Process middle paragraphs (if any)
      while (paragraphs.length > 1) {
        newBlocks.push({
          voice,
          voiceCode,
          speed,
          breakTime,
          id: uuidv4(),
          text: paragraphs.shift(),
          blockStatus: BLOCK_STATUS.DRAFT,
          emphasis,
          hasEmphasis,
        });
      }

      // Last paragraph combines the last part of pasted text with text after cursor
      if (paragraphs.length === 1) {
        newBlocks.push({
          voice,
          voiceCode,
          speed,
          breakTime,
          id: uuidv4(),
          text: paragraphs[0],
          blockStatus: BLOCK_STATUS.DRAFT,
          emphasis,
          hasEmphasis,
        });
      }

      // Update the editor state
      handleChangeContent(
        EditorState.createWithContent(
          ContentState.createFromText(firstParagraphText),
        ),
      );

      // Update current block with first paragraph text
      dispatch(
        actions.project.updateBlock(blockId, {
          text: firstParagraphText,
          voiceCode,
          speed,
          breakTime,
        }),
      );

      // Add new paragraphs
      if (newBlocks.length > 0) {
        onAddNewParagraphs(blockId, newBlocks);
      }
      inputRef.current?.blur();
      return true;
    }
    return false;
  };

  const handleFocusOnBlock = () => {
    onFocusingBlock(blockId);
    inputRef.current?.focus();
  };

  const handleDragUp = () => {
    if (!disabledMoveUp && !isPlaying) {
      onDragEnd(blockId, DIRECTION.UP);
    }
  };

  const handleDragDown = () => {
    if (!isLastBlock && !isPlaying) {
      onDragEnd(blockId, DIRECTION.DOWN);
    }
  };

  const handleDownloadBlockAudio = async () => {
    const response = await getPresignedAudioUrl(requestId);
    if (response?.status) {
      const audioUrl = response.result?.audio;
      const audioName = getFileName(audioLink);
      downloadFileByUrl(audioUrl, `${indexBlock}_${audioName}`);
      trackBlockAudioDownloaded({ requestId });
    }
  };

  const handleOpenErrorReport = () => {
    const ticketMetadata = {
      requestId,
      voiceCode,
      userId,
      blockId,
      projectId,
    };
    const base64TicketMetadata = btoa(JSON.stringify(ticketMetadata));
    const encodedTicketMetadata = encodeURIComponent(base64TicketMetadata);

    window.open(
      `${LANDING_PAGE_URL}/support?metadata=${encodedTicketMetadata}&platform=${PLATFORM_TYPE.DESKTOP_WEB}&category=${CATEGORY_TYPE.PROJECT}`,
      '_blank',
    );
  };

  useEffect(() => {
    if (text) {
      const contentEditor = EditorState.createWithContent(
        ContentState.createFromText(text),
        generateDecorator(BREAK_TIME_REGEX, BreakTimeHighlight),
      );
      handleChangeContent(contentEditor);
    }
  }, []);

  useEffect(() => {
    if (isFocusing) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isFocusing]);

  useEffect(() => {
    dispatch(actions.project.updateBlockStatus({ blockId, blockStatus }));
  }, [blockStatus]);

  const renderConfigBlock = () => (
    <ConfigOptionBlock
      handleOpenVoices={handleOpenVoices}
      handleChangeSpeed={handleChangeSpeed}
      handleChangeBreakTime={handleChangeBreakTime}
      handleChangeEmphasis={handleChangeEmphasis}
      voice={voice}
      speed={speed}
      breakTime={breakTime}
      hasEmphasis={hasEmphasis}
      emphasis={emphasis}
      isSynthesizing={isSynthesizing}
      onSplitBlock={handleSplitBlock}
      canSplitBlock={canSplitBlock}
    />
  );
  const renderCharacterCount = () => (
    <CreditsCount
      creditFactor={voice?.creditFactor}
      exceedBlockMaxCredits={exceedBlockMaxChar}
      credits={countTextLength(text)}
      maxCredits={maxCharLimit}
    />
  );

  const renderDrag = () => (
    <div className="drag drag-new-ui-v2 drag-new-ui">
      <div
        onClick={handleDragUp}
        disabled={
          disabledMoveUp ||
          isPlaying ||
          isFilteringBlockStatus ||
          isFilteringBlockVoice
        }
        role="button"
        tabIndex="-1"
        className={classNames('drag-icon-wrapper', {
          'disabled-move':
            disabledMoveUp ||
            isPlaying ||
            isFilteringBlockStatus ||
            isFilteringBlockVoice,
        })}
      >
        <div
          onClick={handleDragUp}
          disabled={disabledMoveUp || isPlaying || isFilteringBlockStatus}
          role="button"
          tabIndex="-1"
          className={classNames('drag-icon-wrapper', {
            'disabled-move':
              disabledMoveUp || isPlaying || isFilteringBlockStatus,
          })}
        >
          <ArrowUpIcon style={{ color: COLOR.secondary['64'] }} />
        </div>
      </div>
      <div
        onClick={handleDragDown}
        disabled={
          isLastBlock ||
          isPlaying ||
          isFilteringBlockStatus ||
          isFilteringBlockVoice
        }
        role="button"
        tabIndex="-1"
        className={classNames('drag-icon-wrapper', {
          'disabled-move':
            isLastBlock ||
            isPlaying ||
            isFilteringBlockStatus ||
            isFilteringBlockVoice,
        })}
      >
        <ArrowDropDownIcon style={{ color: COLOR.secondary['64'] }} />
      </div>
    </div>
  );

  const renderVoice = () => (
    <div className="voice">
      <Tooltip arrow title={t('chooseVoice')} placement="top">
        <IconButton
          disabled={isSynthesizing}
          onClick={(e) => {
            e.stopPropagation();
            handleOpenVoices();
          }}
        >
          <BadgeAvatar
            width={25}
            smallImgWidth={10}
            img={voice?.roundImage}
            smallImg={voice?.language?.roundImage}
            type="image"
          />
        </IconButton>
      </Tooltip>
    </div>
  );
  const selectedBlockIds = useSelector(
    (state) => state.project.selectedBlockIds,
  );
  const isSelected = useMemo(
    () => selectedBlockIds.includes(blockId),
    [selectedBlockIds, blockId],
  );
  const handleSelectBlock = () => {
    if (isSelected) {
      dispatch(actions.project.unselectBlocks([blockId]));
      return;
    }
    dispatch(actions.project.selectBlocks([blockId]));
  };

  const handleMergeBlock = () => {
    const mergedText = onMergeBlock(blockId);
    if (mergedText) {
      // Update editor state for current block
      const newContentState = ContentState.createFromText(mergedText);
      const newEditorState = EditorState.createWithContent(
        newContentState,
        generateDecorator(BREAK_TIME_REGEX, BreakTimeHighlight),
      );
      handleChangeContent(newEditorState);
    }
  };

  const renderSelectBlock = () => (
    <>
      <div className="select-block select-block-new-ui-v2">
        <Checkbox
          checked={isSelected}
          onChange={handleSelectBlock}
          className="checkbox-color"
        />
      </div>
    </>
  );

  const renderMergeBlockTooltip = () => {
    if (!areBlocksConsecutive)
      return (
        <NewTypography
          variant="body-14-medium"
          dangerouslySetInnerHTML={{
            __html: sanitize(t('cannotMergeBecauseNotConsecutive'), {
              allowedTags: ['span', 'br'],
            }),
          }}
        />
      );

    if (isNextBlockSynthesizing || isSynthesizing)
      return (
        <NewTypography
          variant="body-14-medium"
          dangerouslySetInnerHTML={{
            __html: sanitize(t('cannotMergeBecauseExistBlockIsSynthesizing'), {
              allowedTags: ['span', 'br'],
            }),
          }}
        />
      );

    return null;
  };

  const renderMergeBlockButton = () => (
    <span style={{ display: 'inline-block' }}>
      <CommonButton
        onClick={handleMergeBlock}
        variant="transparent"
        disabled={cannotMerge}
        startIcon={<MergeIcon sx={{ width: '20px', height: '20px' }} />}
        customStyle={{
          height: '24px',
          textTransform: 'none',
          borderRadius: '480px',
          border: `1px solid ${COLOR.indigo[32]}`,
          backgroundColor: COLOR.white,
          fontWeight: 500,
          fontSize: '12px',
          lineHeight: '16px',
          letterSpacing: '-0.4px',
          textAlign: 'center',
          verticalAlign: 'middle',
          padding: '4px 16px',
          '&:hover': {
            backgroundColor: COLOR.whiteV2['64'],
            boxShadow: 'none',
          },
        }}
        textProps={{
          variantText: 'body-12-medium',
          color: COLOR.indigo,
        }}
      >
        {t('mergeBlock')}
      </CommonButton>
    </span>
  );

  return (
    <StyledContentBlockWrapper>
      <StyledContentBlock
        isLastParagraphId={isLastBlock}
        isFocusingOnBlock={isFocusing || isSelected}
        isError={
          exceedBlockMaxChar ||
          hasInvalidCharacterLength ||
          hasInvalidCharacters
        }
        isHighlight={isHighlight}
      >
        {renderSelectBlock()}
        <StyledOrdinalNumberBlock>
          {/* Time Frame Row for Dubbing Projects */}

          <StyledInfoBlock>
            <NewTypography
              color={COLOR.descriptionUpload}
              variant="body-14-regular"
            >
              {indexBlock}.
            </NewTypography>

            <EditNameBlock blockId={blockId} title={title} />
          </StyledInfoBlock>

          <div className="block-wrapper">
            <div className="block-toolbar-wrapper">
              {renderConfigBlock()}
              {renderCharacterCount()}
            </div>
            <div className="editor-wrapper editor-wrapper-new-ui">
              <StyledBlockEditor
                onClick={handleFocusOnBlock}
                role="button"
                tabIndex="-1"
                isFocusingOnBlock={isFocusing}
              >
                {renderVoice()}
                <div
                  className={classNames('editor-container', {
                    'character-exceed': characterExceed,
                  })}
                >
                  <Editor
                    placeholder={t('enterTextHere')}
                    editorState={content}
                    handlePastedText={handlePastedText}
                    handleKeyCommand={handleKeyCommand}
                    keyBindingFn={keyBindingFn}
                    stripPastedStyles
                    onChange={handleEditorChange}
                    ref={inputRef}
                    readOnly={isSynthesizing}
                  />
                </div>
              </StyledBlockEditor>
              <div className="editor-toolbar editor-toolbar-new-ui">
                {(hasInvalidCharacterLength ||
                  hasInvalidCharacters ||
                  exceedBlockMaxChar) && (
                  <Tooltip
                    arrow
                    title={
                      <Typography
                        component="div"
                        sx={{
                          fontSize: '14px',
                          textAlign: 'left',
                          display: 'block',
                        }}
                        dangerouslySetInnerHTML={{
                          __html: (() => {
                            const { message, sanitizeOptions } =
                              getBlockErrorConfig();
                            return sanitize(message, sanitizeOptions);
                          })(),
                        }}
                      />
                    }
                    placement="left"
                  >
                    <div
                      style={{
                        width: '40px',
                        height: '40px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                      className="exceed-block-char-icon"
                    >
                      <WarningRoundedIcon
                        sx={{ color: COLOR.danger, fontSize: '24px' }}
                      />
                    </div>
                  </Tooltip>
                )}
                <SynthesisButton
                  blockId={blockId}
                  status={status}
                  isPlaying={isPlayingBlock}
                  isDisableConvertBtn={isDisableConvertBtn}
                  onSaveProject={onSaveProject}
                  showSuccessIcon={showSuccessIcon}
                  audioExpiredAt={audioExpiredAt}
                  onOpenOutOfCreditsDialog={onOpenOutOfCreditsDialog}
                  onOpenWrongSyntaxDialog={onOpenWrongSyntaxDialog}
                  onOpenWrongSyntaxDialogV2={onOpenWrongSyntaxDialogV2}
                  checkValidCreateSynthesis={checkValidCreateSynthesis}
                  handlePlayAudio={handlePlayAudio}
                  onOpenDiscardedVoiceCloningDialog={
                    onOpenDiscardedVoiceCloningDialog
                  }
                  isDisablePlayAudio={isDisablePlayAudio}
                  onConvertBlockToAudio={onConvertBlockToAudio}
                  isBlockEditting={isBlockEditting}
                  enableCreditUsagePreview={enableCreditUsagePreview}
                />
                <MoreOptionBlock
                  openMoreOptionBlock={openPopperBlockOption}
                  onOpenDeleteDialog={handleOpenDeleteDialog}
                  onOpenMoreOptionBlock={setOpenPopperBlockOption}
                  onOpenReportDialog={handleOpenErrorReport}
                  isDisabledDownloadBlockAudio={isDisablePlayAudio}
                  onDownloadBlockAudio={handleDownloadBlockAudio}
                  isDisabledDeleteBlock={false}
                />
              </div>
            </div>
          </div>
        </StyledOrdinalNumberBlock>
        {renderDrag()}
        {openVoices && (
          <VoiceSelectionDialog
            open={openVoices}
            activeVoiceId={voice && voice.id}
            activeVoiceCode={voice && voice.code}
            voiceStatus={voice && voice.status}
            onClose={handleCloseVoices}
            onChangeVoice={handleChangeVoice}
            hasLanguageFilter
          />
        )}
      </StyledContentBlock>
      {!isLastBlock && (
        <StyledAddNewBlockZone>
          <div className="zone-wrapper">
            <div className="zone-wrapper-left" />
            <Typography
              variant="body2"
              color="text.secondary"
              className="command-to-add-new-block"
            >
              {t('commandToAddNewBlock')}
            </Typography>
            <hr className="border" />
            <>
              {cannotMerge ? (
                <Tooltip
                  arrow
                  title={renderMergeBlockTooltip()}
                  placement="top"
                  maxWidth="550px"
                >
                  {renderMergeBlockButton()}
                </Tooltip>
              ) : (
                renderMergeBlockButton()
              )}
            </>
            <Button
              onClick={handleAddNewParagraphs}
              className="add-btn"
              variant="contained"
              startIcon={
                <img className="add-btn-icon" src={addBlock} alt="icon" />
              }
            >
              {t('addMoreBlock')}
            </Button>
            <div className="zone-wrapper-right" />
          </div>
        </StyledAddNewBlockZone>
      )}
    </StyledContentBlockWrapper>
  );
};

function arePropsEqual(prevProps, nextProps) {
  return (
    prevProps.blockId === nextProps.blockId &&
    prevProps.characterExceed === nextProps.characterExceed &&
    prevProps.isLastBlock === nextProps.isLastBlock &&
    prevProps.isFocusing === nextProps.isFocusing &&
    prevProps.block === nextProps.block &&
    prevProps.disabledMoveUp === nextProps.disabledMoveUp &&
    prevProps.voiceDefault === nextProps.voiceDefault &&
    prevProps.listVoice === nextProps.listVoice &&
    prevProps.isPlaying === nextProps.isPlaying &&
    prevProps.isDownloading === nextProps.isDownloading &&
    prevProps.isHighlight === nextProps.isHighlight &&
    prevProps.projectId === nextProps.projectId &&
    prevProps.isPlayingBlock === nextProps.isPlayingBlock &&
    prevProps.originalBlock === nextProps.originalBlock &&
    prevProps.audioType === nextProps.audioType &&
    prevProps.nextBlock === nextProps.nextBlock &&
    prevProps.prevBlock === nextProps.prevBlock
  );
}

export default memo(ContentBlock, arePropsEqual);
